import React, { useEffect, useState, useRef } from 'react';
import { Globe, Check } from 'lucide-react';

const LANGUAGES = [
  { code: 'fr', label: 'Français', native: 'Français' },
  { code: 'en', label: 'Anglais', native: 'English' },
  { code: 'sw', label: 'Swahili', native: 'Kiswahili' },
  { code: 'ln', label: 'Lingala', native: 'Lingála' },
  { code: 'de', label: 'Allemand', native: 'Deutsch' },
  { code: 'ar', label: 'Arabe', native: 'العربية' },
  { code: 'nl', label: 'Néerlandais', native: 'Nederlands' },
];

const COOKIE_NAME = 'googtrans';

function getCurrentLangFromCookie() {
  const match = document.cookie.match(/googtrans=\/[^/]+\/([a-z-]+)/i);
  if (match && match[1]) return match[1];
  return 'fr';
}

function setLanguageCookie(langCode) {
  // Clear previous cookies on root and host
  const host = window.location.hostname;
  const expires = new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toUTCString();

  // Delete on all common scopes
  document.cookie = `${COOKIE_NAME}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
  document.cookie = `${COOKIE_NAME}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=${host}`;
  document.cookie = `${COOKIE_NAME}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=.${host}`;

  if (langCode && langCode !== 'fr') {
    const value = `/fr/${langCode}`;
    document.cookie = `${COOKIE_NAME}=${value}; expires=${expires}; path=/`;
    document.cookie = `${COOKIE_NAME}=${value}; expires=${expires}; path=/; domain=${host}`;
    document.cookie = `${COOKIE_NAME}=${value}; expires=${expires}; path=/; domain=.${host}`;
  }
}

export default function LanguageSwitcher() {
  const [isOpen, setIsOpen] = useState(false);
  const [currentLang, setCurrentLang] = useState(() => getCurrentLangFromCookie());
  const dropdownRef = useRef(null);

  useEffect(() => {
    if (window.__googleTranslateLoaded) return;
    window.__googleTranslateLoaded = true;

    window.googleTranslateElementInit = () => {
      new window.google.translate.TranslateElement(
        {
          pageLanguage: 'fr',
          includedLanguages: LANGUAGES.map((l) => l.code).join(','),
          layout: window.google.translate.TranslateElement.InlineLayout.SIMPLE,
          autoDisplay: false,
        },
        'google_translate_element_hidden'
      );
    };

    const script = document.createElement('script');
    script.src = '//translate.google.com/translate_a/element.js?cb=googleTranslateElementInit';
    script.async = true;
    document.body.appendChild(script);

    // Aggressively keep body at top:0 — Google Translate keeps trying to push
    // the body down to make room for its banner. We hide the banner via CSS but
    // also reset any inline `top` style it may set on the body/html.
    const resetTop = () => {
      if (document.body.style.top && document.body.style.top !== '0px' && document.body.style.top !== '') {
        document.body.style.top = '0px';
      }
      if (document.documentElement.style.marginTop) {
        document.documentElement.style.marginTop = '0';
      }
    };
    const observer = new MutationObserver(resetTop);
    observer.observe(document.body, { attributes: true, attributeFilter: ['style', 'class'] });
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['style', 'class'] });
    const interval = setInterval(resetTop, 1000);
    return () => { observer.disconnect(); clearInterval(interval); };
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const changeLanguage = (langCode) => {
    if (langCode === currentLang) {
      setIsOpen(false);
      return;
    }
    setLanguageCookie(langCode);
    setCurrentLang(langCode);
    setIsOpen(false);

    // Try instant translation via Google Translate's hidden combo box
    // (no page reload needed once the script is loaded)
    const combo = document.querySelector('.goog-te-combo');
    if (combo) {
      combo.value = langCode === 'fr' ? '' : langCode;
      combo.dispatchEvent(new Event('change'));
      // Force a re-render of any cached DOM
      setTimeout(() => {
        if (langCode === 'fr') {
          // Going back to source language requires reload
          window.location.reload();
        }
      }, 200);
      return;
    }
    // Fallback when the GT script hasn't loaded yet
    window.location.reload();
  };

  const current = LANGUAGES.find((l) => l.code === currentLang) || LANGUAGES[0];

  return (
    <div className="relative notranslate" ref={dropdownRef} translate="no">
      <div id="google_translate_element_hidden" style={{ display: 'none' }}></div>
      <button
        onClick={() => setIsOpen(!isOpen)}
        data-testid="language-switcher-button"
        className="flex items-center gap-2 px-3 py-2 text-sm border-2 border-[#4A6B48] rounded-lg text-[#4A6B48] hover:bg-[#4A6B48] hover:text-white transition-all font-semibold notranslate"
      >
        <Globe size={16} />
        <span>{current.native}</span>
      </button>
      {isOpen && (
        <div className="absolute right-0 mt-2 w-52 bg-white border-2 border-[#E5E3DB] rounded-lg shadow-xl z-50 overflow-hidden notranslate">
          {LANGUAGES.map((lang) => (
            <button
              key={lang.code}
              onClick={() => changeLanguage(lang.code)}
              data-testid={`language-option-${lang.code}`}
              className={`flex w-full items-center justify-between text-left px-4 py-2 text-sm transition-colors hover:bg-[#F9F8F6] ${
                currentLang === lang.code ? 'bg-[#4A6B48]/10 font-bold text-[#4A6B48]' : 'text-[#2E332D]'
              }`}
            >
              <span>
                <span className="font-medium">{lang.native}</span>
                {lang.label !== lang.native && (
                  <span className="text-xs text-[#5C665A] ml-2">({lang.label})</span>
                )}
              </span>
              {currentLang === lang.code && <Check size={14} className="text-[#4A6B48]" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
